import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

function CountUp({ value, prefix = "", suffix = "" }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const target = Number(value) || 0;
    const start = performance.now();
    const duration = 1200;
    function tick(now) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(target * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);
  return (
    <span>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>
  );
}

const ITEMS = [
  { key: "years_active", label: "Years in the community", suffix: "" , color: "#F2A93B" },
  { key: "programs", label: "Active programs", color: "#31517E" },
  { key: "volunteers", label: "Volunteer applications", color: "#C05A3A" },
  { key: "funds_raised_usd", label: "Raised for programs", prefix: "$", color: "#1F5C38" },
  { key: "newsletter_subscribers", label: "Subscribers on the list", color: "#A62639" },
];

export default function ImpactTicker() {
  const [data, setData] = useState(null);
  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    api.get("/impact").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="py-16 bg-white border-y border-ink/10" ref={containerRef} data-testid="impact-ticker">
      <div className="max-w-[1140px] mx-auto px-6">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2.5 text-[13px] font-bold uppercase tracking-[0.14em] text-palm">
              <span className="w-6 h-[3px] rounded-sm bg-mango" />
              Live impact
            </span>
            <h2 className="mt-3 text-[clamp(28px,3.4vw,40px)]">ERSEH in numbers.</h2>
          </div>
          <p className="text-ink/60 text-sm max-w-[38ch]">
            Updated from HOFA's live database — every donor, subscriber, and volunteer application counts.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {ITEMS.map((it) => {
            const raw = data ? data[it.key] : null;
            const val = it.key === "funds_raised_usd" ? Math.round(raw || 0) : raw || 0;
            return (
              <div
                key={it.key}
                data-testid={`impact-${it.key}`}
                className="relative rounded-[14px] p-5 bg-paper border border-ink/10 overflow-hidden"
              >
                <span className="absolute top-0 left-0 right-0 h-1" style={{ background: it.color }} />
                <div className="font-display text-3xl md:text-4xl leading-none pt-2 text-ink">
                  {inView && data ? <CountUp value={val} prefix={it.prefix} suffix={it.suffix} /> : (
                    <span>{it.prefix || ""}0{it.suffix || ""}</span>
                  )}
                </div>
                <div className="mt-2 text-sm text-ink/65">{it.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
